import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import CustomPasswordTextField from "../../common/CustomPasswordTextField";
import { FAILED_CODE } from "../../../service/ServiceConstants";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  downloadDocumentById,
  validateManufacturerCredentials,
} from "../../../redux/slice/manufacturerNotifications/ManufacturerNotificationSlice";
import { decryptAES } from "../../../utils/EncryptorUtils";
import { useModalStyles } from "../../common/useModalStyles";
import { Download } from "lucide-react";

interface ManufacturerNotificationIVRDownloadProps {
  onClose: () => void;
}

const ManufacturerNotificationIVRDownload: React.FC<
  ManufacturerNotificationIVRDownloadProps
> = ({ onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens: modalTokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    primaryBtnSx,
    inputFieldWithLabelSx,
  } = useModalStyles();

  const location = useLocation();
  const dispatch = useAppDispatch();
  const { control } = useForm();

  const [password, setPassword] = React.useState("");
  const [ivrDocId, setIvrDocId] = useState("");
  const [ivrNumber, setIvrNumber] = useState("");
  const [email, setEmail] = useState("");
  const [orderDocId, setorderDocId] = useState("");
  const [orderNumber, setorderNumber] = useState("");
  const [tissueTrackingDocId, setTissueTrackingDocId] = useState("");
  const [productReturnDocId, setProductReturnDocId] = useState("");
  const [errorMessageContent, setErrorMessageContent] = React.useState("");
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const encryptedIvrDocId = params.get("ivrDocId") || null;
    const encryptedIvrNumber = params.get("ivrNumber") || null;
    const encryptedOrderDocId = params.get("orderDocId") || null;
    const encryptedOrderNumber = params.get("orderNumber") || null;
    const encryptedTissueTrackingDocId = params.get("tissueTrackingDocId") || null;
    const encryptedProductReturnDocId = params.get("productReturnDocId") || null;
    const encryptedEmail = params.get("email") || null;
    const iv = params.get("iv");
    const salt = params.get("salt");

    if (encryptedIvrDocId != null && encryptedIvrDocId !== "") {
      setIvrDocId(decryptAES(encryptedIvrDocId, iv, salt));
    }
    if (encryptedIvrNumber != null && encryptedIvrNumber !== "") {
      setIvrNumber(decryptAES(encryptedIvrNumber, iv, salt));
    }
    if (encryptedOrderDocId != null && encryptedOrderDocId !== "") {
      setorderDocId(decryptAES(encryptedOrderDocId, iv, salt));
    }
    if (encryptedOrderNumber != null && encryptedOrderNumber !== "") {
      setorderNumber(decryptAES(encryptedOrderNumber, iv, salt));
    }
    if (encryptedTissueTrackingDocId != null && encryptedTissueTrackingDocId !== "") {
      setTissueTrackingDocId(decryptAES(encryptedTissueTrackingDocId, iv, salt));
    }
    if (encryptedProductReturnDocId != null && encryptedProductReturnDocId !== "") {
      setProductReturnDocId(decryptAES(encryptedProductReturnDocId, iv, salt));
    }
    if (encryptedEmail != null && encryptedEmail !== "") {
      setEmail(decryptAES(encryptedEmail, iv, salt));
    }
  }, [location.search]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const pwd: string = data.get("password") as string;

    handleLogin(email, pwd);
  };

  const handleLogin = async (userName: string, pwd: string) => {
    if (userName && userName.trim() !== "" && pwd) {
      if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(userName)) {
        setErrorMessageContent("Invalid email address.");
        setOpen(true);
        return;
      }
      try {
        const response = await dispatch(
          validateManufacturerCredentials({
            loginInfo: { userName, password: pwd },
            interceptorOption: { skipErrorPopup: true },
          })
        ).unwrap();

        if (response) {
          onClose();
        }
      } catch (e: any) {
        if (e?.code === FAILED_CODE) {
          setErrorMessageContent(e?.message);
        } else if (e?.message === "Rejected") {
          setErrorMessageContent("Unknown error. Please contact support.");
        } else {
          setErrorMessageContent("Unknown error. Please contact support.");
        }
        setOpen(true);
      }
    } else {
      setErrorMessageContent("Please enter both email and password.");
      setOpen(true);
    }
  };

  const handleDismissAlert = () => {
    setOpen(false);
  };

  const downloadTitle =
    ivrDocId
      ? `IVR (${ivrNumber})`
      : orderDocId
        ? `Order (${orderNumber})`
        : tissueTrackingDocId
          ? `Tissue Tracking`
          : productReturnDocId
            ? `Product Return`
            : "";

  const accessKind =
    ivrDocId
      ? "IVR"
      : orderDocId
        ? "Order"
        : tissueTrackingDocId
          ? "Tissue Tracking"
          : productReturnDocId
            ? "Product Return"
            : "";

  return (
    <Dialog
      open={true}
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onClose();
        }
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          ...paperSx(isMobile),
          minWidth: { xs: "100%", sm: 400 },
        },
      }}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(2px)",
        },
      }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(modalTokens.accent)}>
          <Download size={22} color={modalTokens.accent} strokeWidth={1.8} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: modalTokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            Download {downloadTitle}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: modalTokens.textSecondary, mt: 0.15 }}>
            Please enter your password for Access {accessKind}
          </Typography>
        </Box>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box sx={{ ...bodySx, minHeight: 0 }}>
          {errorMessageContent != null && errorMessageContent !== "" && open && (
            <Box sx={{ mb: 0 }}>
              <Alert variant="filled" severity="error" onClose={handleDismissAlert}>
                {errorMessageContent}
              </Alert>
            </Box>
          )}

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: modalTokens.textSecondary, mb: 0.5 }}>
              Email
            </Typography>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: modalTokens.textPrimary, wordBreak: "break-all" }}>
              {email}
            </Typography>
          </Box>

          <Box sx={inputFieldWithLabelSx(false)}>
            <CustomPasswordTextField
              control={control}
              margin="normal"
              fullWidth
              controllerName="password"
              label="Password"
              id="password"
              name="password"
              autoComplete="current-password"
              sx={{ mt: 0 }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Box>
        </Box>

        <Box sx={footerSx}>
          <Button type="submit" variant="contained" fullWidth sx={primaryBtnSx}>
            Access
            {ivrDocId
              ? " IVR"
              : orderDocId
                ? " Order"
                : tissueTrackingDocId
                  ? " Tissue Tracking"
                  : productReturnDocId
                    ? " Product Return"
                    : ""}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ManufacturerNotificationIVRDownload;
